import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Ban, Check, X, type LucideIcon } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Field';
import { cn } from '../../../lib/cn';
import { addTag, listAnnouncement, removeAt, TAG_RULE_TEXT } from '../tagRules';

/** The colour of a list's tags (spec:263): teal, Always say green with a check, Never say red with a slash. */
export type TagTone = 'teal' | 'always' | 'never';

const TONE_CLASS: Record<TagTone, string> = {
  teal: 'bg-teal-tint text-teal-fg',
  always: 'bg-green-50 text-green-800 dark:bg-green-950/60 dark:text-green-300',
  never: 'bg-red-50 text-red-700 dark:bg-error-dark-tint dark:text-error-dark',
};
// An icon besides the colour, so no list relies on colour alone (spec:263).
const TONE_ICON: Partial<Record<TagTone, LucideIcon>> = { always: Check, never: Ban };

/**
 * A Vocabulary tag cloud (spec:261-263; web.md §3.2, §3.4): the tags, each with a remove button, then an
 * "Add…" box with Add. Enter or Add adds the typed entry by the tag rules; typing alone is never a
 * change (spec:197): `onChange` fires only when a tag is added or removed. A polite live region says what
 * happened. Stored tags that break today's rules (E4) are shown as they are and can be removed.
 *
 * `id` is the add box's: the field's `<label for>` names it. Read-only shows the tags only.
 */
export function TagListInput({
  id,
  label,
  addPlaceholder,
  items,
  onChange,
  tone,
  normalize,
  maxItems,
  maxLength,
  readOnly = false,
  disabled = false,
  describedBy,
}: {
  id: string;
  /** The list's name ("Keywords"), for the list and the Add button. */
  label: string;
  addPlaceholder: string;
  items: readonly string[];
  onChange: (next: string[]) => void;
  tone: TagTone;
  /** How a typed entry is cleaned up (normalizeHashtag for Social hashtags). */
  normalize?: (raw: string) => string;
  maxItems: number;
  maxLength: number;
  readOnly?: boolean;
  disabled?: boolean;
  describedBy?: string;
}) {
  const [draft, setDraft] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const removeButtons = useRef(new Map<number, HTMLButtonElement>());
  // After a removal, where the focus goes once the list has re-rendered: the tag now at that place,
  // else the one before it, else the add box.
  const [focusAfterRemove, setFocusAfterRemove] = useState<number | null>(null);

  useEffect(() => {
    if (focusAfterRemove === null) return;
    const target =
      removeButtons.current.get(focusAfterRemove) ?? removeButtons.current.get(focusAfterRemove - 1) ?? input.current;
    target?.focus();
    setFocusAfterRemove(null);
  }, [focusAfterRemove, items]);

  const full = items.length >= maxItems;
  const problemId = `${id}-problem`;
  const Icon = TONE_ICON[tone];

  const add = () => {
    const outcome = addTag(items, draft, { normalize, maxItems, maxLength });
    if ('items' in outcome) {
      onChange(outcome.items);
      setAnnouncement(listAnnouncement.added(outcome.items[outcome.items.length - 1]));
      setDraft('');
      setProblem(null);
    } else if ('duplicate' in outcome) {
      setAnnouncement(listAnnouncement.duplicate(outcome.duplicate));
      setDraft('');
      setProblem(null);
    } else if (outcome.error === 'tooLong') {
      setProblem(TAG_RULE_TEXT.tooLong);
    } else if (outcome.error === 'full') {
      setProblem(TAG_RULE_TEXT.full);
    }
  };

  const remove = (index: number) => {
    const item = items[index];
    onChange(removeAt(items, index));
    setAnnouncement(listAnnouncement.removed(item));
    setProblem(null);
    setFocusAfterRemove(index);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    add();
  };

  const describedByInput = [describedBy, problem || full ? problemId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div>
      {items.length > 0 ? (
        <ul aria-label={label} className="flex flex-wrap gap-2">
          {items.map((item, index) => (
            // By position and text: a stored list may hold the same text twice (E4).
            <li key={`${index}:${item}`} className="max-w-full">
              <span
                className={cn(
                  'inline-flex max-w-full items-center gap-1 rounded-full py-1 text-xs font-medium',
                  readOnly ? 'px-2.5' : 'pl-2.5 pr-1',
                  TONE_CLASS[tone],
                )}
              >
                {Icon && <Icon className="h-3 w-3 shrink-0" aria-hidden />}
                <span className="min-w-0 break-words">{item}</span>
                {!readOnly && (
                  <button
                    type="button"
                    ref={(el) => {
                      if (el) removeButtons.current.set(index, el);
                      else removeButtons.current.delete(index);
                    }}
                    disabled={disabled}
                    onClick={() => remove(index)}
                    aria-label={`Remove ${item}`}
                    className="focus-ring flex h-5 w-5 shrink-0 items-center justify-center rounded-full opacity-70 hover:opacity-100 disabled:opacity-40"
                  >
                    <X className="h-3 w-3" aria-hidden />
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      ) : readOnly ? (
        <p className="text-sm text-ink-faint">Not provided</p>
      ) : null}

      {!readOnly && (
        <div className={cn('flex gap-2', items.length > 0 && 'mt-3')}>
          <Input
            ref={input}
            id={id}
            value={draft}
            disabled={disabled || full}
            placeholder={addPlaceholder}
            aria-describedby={describedByInput}
            aria-invalid={problem === TAG_RULE_TEXT.tooLong ? true : undefined}
            onChange={(e) => {
              setDraft(e.target.value);
              if (problem === TAG_RULE_TEXT.tooLong) setProblem(null);
            }}
            onKeyDown={onKeyDown}
            className="min-w-0 flex-1"
          />
          <Button
            type="button"
            variant="secondary"
            disabled={disabled || full}
            onClick={add}
            aria-label={`Add to ${label}`}
          >
            Add
          </Button>
        </div>
      )}
      {!readOnly && (problem || full) && (
        <p
          id={problemId}
          className={cn(
            'mt-1 text-xs',
            problem === TAG_RULE_TEXT.tooLong ? 'text-red-600 dark:text-error-dark' : 'text-ink-muted',
          )}
        >
          {full ? TAG_RULE_TEXT.full : problem}
        </p>
      )}
      <p className="sr-only" aria-live="polite">
        {announcement}
      </p>
    </div>
  );
}
